// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient, TyOrganizationBrief } from "@/types";
import { readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import PhoneInput from "@/UI/inputs/PhoneInput";
import TextInput from "@/UI/inputs/TextInput";
import PasswordPopup from "@/UI/popups/PasswordPopup";
// --- Types --------------------------------------------------------------------------------------
export type TyOwnerFormDetails = {
    organization: TyOrganizationBrief;
    /** What an enquiry already told us, when this form was opened from one. A starting point only. */
    prefill?: { name?: string; phone?: string };
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * The first owner of a company, and **the only account this product creates from the admin side.**
 *
 * A company is created empty and its own team screen needs somebody signed in to reach — so without
 * this, a company Lozicore just sold could never be opened by anybody. Everyone after this one is
 * added by the owner, which is why there is no role to pick here: this seat is the only one.
 *
 * The phone decides what happens, not a choice on the form. A number nobody has used creates an
 * account and hands back a password once; a number that already belongs to somebody hands them the
 * company and leaves their password alone — they are one person across every company they work in.
 */
export default function OwnerFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const addComp = usePopup((state) => state.addComp);
    const { organization, prefill, onDone } = (details ?? {}) as TyOwnerFormDetails;

    const company = readLangValue(organization.name, lang) || organization.slug;

    const [name, setName] = useState(prefill?.name ?? "");
    const [phone, setPhone] = useState<string | undefined>(prefill?.phone ?? "");
    const [email, setEmail] = useState("");
    const [formError, setFormError] = useState<string | null>(null);

    const save = useFetch<{ client: TyClient; password: string | null }>(`/organizations/${organization._id}/owner`, { method: "POST" }, false);

    useEffect(() => {
        if (!save.succeededAt) return;
        onDone?.();
        onClose();

        /** A created account's password exists in this response and nowhere else, ever again. */
        const password = save.data?.password;
        if (password) {
            addComp({ name: "password", Component: PasswordPopup, anim: "scale", details: { name: save.data?.client?.name ?? name, password } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const handleSubmit = () => {
        setFormError(null);

        // The country picker leaves the calling code in place, so an untouched field is "+966" alone.
        const cleanPhone = (phone ?? "").replace(/\s/g, "");
        if (cleanPhone.length < 8) return setFormError(t.auth.phoneRequired);

        save.invoke({ method: "POST", data: { phone: cleanPhone, name: name.trim(), email: email.trim() || null } });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.organizations.ownerTitle.replace("{name}", company)}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="side-form__hint">{t.organizations.ownerHint}</p>

                {/* The person's own fields, named the way the people section names them. */}
                <PhoneInput value={phone} onChange={setPhone} label={t.clients.fields.phone} disabled={save.loading} />
                <TextInput value={name} onChange={setName} label={t.clients.fields.name} disabled={save.loading} />
                <p className="side-form__hint">{t.organizations.ownerNameHint}</p>

                <TextInput value={email} onChange={setEmail} label={t.clients.fields.email} type="email" dir="ltr" disabled={save.loading} />

                {(formError || save.error) && <p className="side-form__error">{formError ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.organizations.setOwner}
                </button>
            </footer>
        </section>
    );
}
